import { createContext, useContext, useState, useEffect, useCallback } from 'react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_V2 = `${BACKEND_URL}/api/v2`;
const TOKEN_KEY = 'bitbot_session_token';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within an AuthProvider');
  return context;
};

// v3.9.17: Token storage helpers — persistent Bearer token for iOS cookie-loss scenarios.
const getStoredToken = () => {
  try { return localStorage.getItem(TOKEN_KEY); } catch { return null; }
};
const setStoredToken = (t) => {
  try {
    if (t) localStorage.setItem(TOKEN_KEY, t);
    else localStorage.removeItem(TOKEN_KEY);
  } catch { /* ignore */ }
};

// XHR wrapper — sends cookies AND Authorization: Bearer <token> as fallback.
// This is the belt-and-suspenders approach that fixes intermittent iOS logins:
// - Cookies work most of the time (fast path)
// - Bearer token guarantees auth even when iOS ITP strips the cookie
function xhrRequest(method, url, body = null) {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open(method, url, true);
    xhr.withCredentials = true;
    if (body) xhr.setRequestHeader('Content-Type', 'application/json');
    const token = getStoredToken();
    if (token) xhr.setRequestHeader('Authorization', `Bearer ${token}`);
    xhr.onload = function () {
      let data = null;
      try { data = JSON.parse(xhr.responseText); } catch (e) { /* not json */ }
      resolve({ status: xhr.status, ok: xhr.status >= 200 && xhr.status < 300, data, text: xhr.responseText });
    };
    xhr.onerror = function () { reject(new Error('Network request failed')); };
    xhr.send(body ? JSON.stringify(body) : null);
  });
}

// Also wire up global axios to include Bearer token — every API call across the app benefits.
// axios is imported lazily to avoid circular imports.
if (typeof window !== 'undefined') {
  import('axios').then(({ default: axios }) => {
    axios.defaults.withCredentials = true;
    axios.interceptors.request.use((config) => {
      const t = getStoredToken();
      if (t) {
        config.headers = config.headers || {};
        if (!config.headers.Authorization) {
          config.headers.Authorization = `Bearer ${t}`;
        }
      }
      return config;
    });
    axios.interceptors.response.use(
      (r) => r,
      (err) => {
        // On 401, wipe the stale token so the next login can replace it
        if (err?.response?.status === 401) {
          setStoredToken(null);
        }
        return Promise.reject(err);
      }
    );
  }).catch(() => {});
}

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    try {
      const res = await xhrRequest('GET', `${API_V2}/auth/me`);
      if (res.ok && res.data) setUser(res.data);
      else setUser(null);
    } catch (error) {
      console.error('Auth check failed:', error);
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const login = () => {
    const redirectUrl = window.location.origin + '/dashboard';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const emergencyLogin = async (email, accessCode) => {
    const res = await xhrRequest('POST', `${API_V2}/auth/emergency-access`, { email, access_code: accessCode });
    if (res.ok && res.data) {
      // v3.9.17: persist token for iOS Bearer fallback
      if (res.data.session_token) setStoredToken(res.data.session_token);
      setUser(res.data);
      return res.data;
    }
    throw new Error(`Emergency login failed (${res.status}): ${res.text}`);
  };

  const logout = async () => {
    try {
      await xhrRequest('POST', `${API_V2}/auth/logout`);
    } catch (error) {
      console.error('Logout failed:', error);
    }
    setStoredToken(null);
    setUser(null);
  };

  const processSession = async (sessionId) => {
    try {
      const res = await xhrRequest('POST', `${API_V2}/auth/session`, { session_id: sessionId });
      if (res.ok && res.data) {
        // v3.9.17: persist token for iOS Bearer fallback
        if (res.data.session_token) setStoredToken(res.data.session_token);
        setUser(res.data);
        return res.data;
      }
      throw new Error(`Session processing failed (${res.status}): ${res.text}`);
    } catch (error) {
      console.error('Session processing error:', error);
      throw error;
    }
  };

  return (
    <AuthContext.Provider value={{
      user,
      loading,
      login,
      logout,
      emergencyLogin,
      processSession,
      checkAuth,
      isAuthenticated: !!user
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
